import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Pencil, Trash2, Check, Loader2, Clock } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../context/AuthContext';
import { iniciales, tiempoRelativo } from '../utils/helpers';
import ConfirmDialog from '../ConfirmDialog';

// ----- Render de eventos de actividad -----

const renderActividad = (item) => {
  const p = item.payload || {};
  switch (item.tipo) {
    case 'tarea_creada':
      return <>creó la tarea</>;
    case 'titulo_cambiado':
      return <>renombró la tarea</>;
    case 'estado_cambiado':
      return (
        <>
          movió de <strong className="text-slate-700">{p.antes || '—'}</strong>
          {' '}a <strong className="text-slate-700">{p.despues || '—'}</strong>
        </>
      );
    case 'asignado_cambiado':
      if (!p.despues) return <>quitó la asignación</>;
      if (!p.antes) return <>asignó a <strong className="text-slate-700">{p.despues}</strong></>;
      return <>reasignó a <strong className="text-slate-700">{p.despues}</strong></>;
    case 'asignado_agregado':
      return <>asignó a <strong className="text-slate-700">{p.nombre}</strong></>;
    case 'asignado_quitado':
      return <>quitó a <strong className="text-slate-700">{p.nombre}</strong></>;
    case 'hito_cambiado':
      if (!p.despues) return <>quitó del hito</>;
      return <>movió al hito <strong className="text-slate-700">{p.despues}</strong></>;
    case 'subtarea_asignada':
      return <>asignó la subtarea «{p.texto}» a <strong className="text-slate-700">{p.nombre}</strong></>;
    case 'prioridad_cambiada':
      return (
        <>
          cambió la prioridad a <strong className="text-slate-700">{p.despues}</strong>
        </>
      );
    case 'fecha_cambiada':
      if (!p.despues) return <>quitó la fecha de vencimiento</>;
      return (
        <>
          puso la fecha en{' '}
          <strong className="text-slate-700">
            {new Date(p.despues + 'T00:00:00').toLocaleDateString('es-VE', {
              day: 'numeric', month: 'short',
            })}
          </strong>
        </>
      );
    case 'adjunto_anadido':
      return <>subió <strong className="text-slate-700">{p.nombre || 'un archivo'}</strong></>;
    case 'archivada':
      return <>archivó la tarea</>;
    case 'desarchivada':
      return <>desarchivó la tarea</>;
    default:
      return null;
  }
};

// ----- Item de comentario editable -----

const CommentItem = ({ comentario, esAutor, color, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comentario.contenido);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(draft.length, draft.length);
    }
  }, [editing, draft.length]);

  const handleSave = () => {
    const t = draft.trim();
    if (!t || t === comentario.contenido) {
      setDraft(comentario.contenido);
      setEditing(false);
      return;
    }
    onEdit(comentario.id, t);
    setEditing(false);
  };

  return (
    <div className="group flex gap-2.5">
      <div
        className="w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: `${color}1f`, color }}
      >
        {iniciales(comentario.autor?.nombre)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="text-sm font-medium text-slate-900 truncate">
            {comentario.autor?.nombre || 'Alguien'}
          </span>
          <span className="text-[10px] text-slate-400 tabular-nums">
            {tiempoRelativo(comentario.created_at)}
          </span>
          {esAutor && !editing && (
            <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => { setDraft(comentario.contenido); setEditing(true); }}
                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                title="Editar"
              >
                <Pencil size={11} />
              </button>
              <button
                onClick={() => onDelete(comentario)}
                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                title="Eliminar"
              >
                <Trash2 size={11} />
              </button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="bg-white rounded-md ring-1 ring-cyan-400 ring-offset-0 overflow-hidden">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault(); handleSave();
                } else if (e.key === 'Escape') {
                  setDraft(comentario.contenido); setEditing(false);
                }
              }}
              rows={2}
              className="w-full text-sm text-slate-800 px-2.5 py-1.5 bg-transparent focus:outline-none resize-none"
            />
            <div className="flex items-center justify-end gap-1 px-2 pb-1.5">
              <button
                onClick={() => { setDraft(comentario.contenido); setEditing(false); }}
                className="px-2 py-0.5 text-[11px] text-slate-500 hover:text-slate-800 rounded transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] text-white bg-slate-900 hover:bg-slate-800 rounded transition-colors"
              >
                <Check size={11} />
                Guardar
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
            {comentario.contenido}
          </p>
        )}
      </div>
    </div>
  );
};

// ----- Componente principal -----

const CommentsActivity = ({ tareaId, comentarios = [], actividad = [], color, onChanged, registerFocusRef }) => {
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [comentarioToDelete, setComentarioToDelete] = useState(null);
  const inputRef = useRef(null);
  const historialBtnRef = useRef(null);

  useEffect(() => {
    if (registerFocusRef) registerFocusRef(inputRef);
  }, [registerFocusRef]);

  // Comentarios ordenados cronológicamente
  const stream = [...comentarios].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  // Eventos de actividad relevantes (excluyendo comentario_anadido — ya vivo en el feed)
  const historial = [...actividad]
    .filter((x) => x.tipo !== 'comentario_anadido')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Cerrar popover de historial al click fuera
  useEffect(() => {
    if (!historialOpen) return;
    const onClick = (e) => {
      if (historialBtnRef.current && !historialBtnRef.current.contains(e.target)) {
        setHistorialOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [historialOpen]);

  const submitComment = useCallback(async () => {
    const t = draft.trim();
    if (!t || submitting) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/comentarios`, { contenido: t });
      setDraft('');
      onChanged?.();
    } catch (err) {
      console.error('Error enviando comentario:', err);
    } finally {
      setSubmitting(false);
    }
  }, [draft, submitting, tareaId, onChanged]);

  const handleEdit = useCallback(async (id, contenido) => {
    try {
      await axiosInstance.put(`/api/tareas/${tareaId}/comentarios/${id}`, { contenido });
      onChanged?.();
    } catch (err) {
      console.error('Error editando:', err);
    }
  }, [tareaId, onChanged]);

  const doDelete = useCallback(async () => {
    if (!comentarioToDelete) return;
    const id = comentarioToDelete.id;
    setComentarioToDelete(null);
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/comentarios/${id}`);
      onChanged?.();
    } catch (err) {
      console.error('Error eliminando:', err);
    }
  }, [tareaId, onChanged, comentarioToDelete]);

  const empleadoActual = user?.empleado_id;

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-3">
        <MessageSquare size={11} strokeWidth={2} className="text-slate-400" />
        <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
          Conversación
        </span>
        <div className="flex-1 h-px bg-slate-200/60" />
        {historial.length > 0 && (
          <div className="relative" ref={historialBtnRef}>
            <button
              onClick={() => setHistorialOpen((v) => !v)}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                historialOpen
                  ? 'bg-slate-100 text-slate-700'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
              }`}
              title="Ver historial de cambios"
            >
              <Clock size={10} strokeWidth={2} />
              Historial
              <span className="tabular-nums opacity-70">{historial.length}</span>
            </button>
            <AnimatePresence>
              {historialOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.12 }}
                  className="absolute right-0 top-full mt-1.5 w-80 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 overflow-hidden z-20"
                >
                  <div className="px-3 py-2 border-b border-slate-200/70 flex items-center justify-between">
                    <span className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase">
                      Historial de cambios
                    </span>
                    <span className="text-[10px] text-slate-400 tabular-nums">{historial.length}</span>
                  </div>
                  <ol className="max-h-80 overflow-y-auto py-1">
                    {historial.map((ev) => {
                      const txt = renderActividad(ev);
                      if (!txt) return null;
                      return (
                        <li
                          key={ev.id}
                          className="flex items-start gap-2 px-3 py-1.5 hover:bg-slate-50 transition-colors"
                        >
                          <span className="mt-1 w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                          <span className="text-[11px] text-slate-500 leading-relaxed flex-1 min-w-0">
                            <strong className="font-medium text-slate-700">
                              {ev.autor?.nombre || 'Alguien'}
                            </strong>{' '}
                            {txt}
                          </span>
                          <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0 mt-0.5">
                            {tiempoRelativo(ev.created_at)}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Stream solo comentarios */}
      <ol className="space-y-3 mb-4">
        <AnimatePresence initial={false}>
          {stream.length === 0 ? (
            <li className="text-xs text-slate-400 italic px-1 py-2">
              Aún nadie ha comentado. Sé el primero.
            </li>
          ) : (
            stream.map((item) => (
              <motion.li
                key={`c-${item.id}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <CommentItem
                  comentario={item}
                  esAutor={empleadoActual === item.autor_id}
                  color={color}
                  onEdit={handleEdit}
                  onDelete={setComentarioToDelete}
                />
              </motion.li>
            ))
          )}
        </AnimatePresence>
      </ol>

      {/* Composer */}
      <div className="flex gap-2.5">
        <div
          className="w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${color}1f`, color }}
        >
          {iniciales(user?.nombre_empleado || user?.username)}
        </div>
        <div className="flex-1 bg-white rounded-lg border border-slate-200 focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-cyan-500/15 transition-all overflow-hidden">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submitComment();
              }
            }}
            placeholder="Escribe un comentario..."
            rows={2}
            disabled={submitting}
            className="w-full text-sm text-slate-800 px-3 py-2 bg-transparent focus:outline-none resize-none placeholder-slate-400 disabled:opacity-50"
          />
          <div className="flex items-center justify-between px-3 pb-1.5 text-[10px] text-slate-400">
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1 text-[9px] font-mono font-medium bg-white text-slate-500 border border-slate-200 rounded">Cmd</kbd>
              +
              <kbd className="px-1 text-[9px] font-mono font-medium bg-white text-slate-500 border border-slate-200 rounded">Enter</kbd>
              envía
            </span>
            <button
              type="button"
              onClick={submitComment}
              disabled={!draft.trim() || submitting}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-white bg-slate-900 hover:bg-slate-800 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Comentar
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!comentarioToDelete}
        title="¿Eliminar este comentario?"
        message="Su contenido se perderá. La acción no se puede deshacer."
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={doDelete}
        onClose={() => setComentarioToDelete(null)}
      />
    </section>
  );
};

export default CommentsActivity;
